import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemSessionAttendance from "Reporting/Components/ReportItemSessionAttendance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { Select } from "shared/Select/Select";

export default function ReportTableSessionAttendance({
  reports,
  title,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([]);
  const [sortedReports, setSortedReports] = useState([]);
  const [totalAttendees, setTotalAttendees] = useState([]);

  const getAllOptions = (field) => {
    if (!Array.isArray(reports)) return [];
    return Array.from(new Set(reports.map((r) => r[field]).filter(Boolean)));
  };

  const [selectedFilters, setSelectedFilters] = useState({});

  useEffect(() => {
    setSelectedFilters({
      ticket: getAllOptions("ticket"),
      diamond: getAllOptions("diamond"),
    });
  }, [reports]);

  const getSortValue = (row, field) => {
    switch (field) {
      case "IBO":
        return row.IBO;
      case "Last Name":
        return row.lastName;
      case "First Name":
        return row.firstName;
      case "Ticket":
        return row.ticket;
      case "Diamond":
        return row.diamond;
      default:
        return "";
    }
  };

  useEffect(() => {
    let result = Array.isArray(reports) ? [...reports] : [];

    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (!values || values.length === 0) {
        result = [];
        return;
      }
      result = result.filter((r) => values.includes(r[field]));
    });

    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (s.direction === "none") continue;

          let aVal = getSortValue(a, s.field);
          let bVal = getSortValue(b, s.field);

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
    setTotalAttendees(result.length);
  }, [reports, sort, selectedFilters]);

  const prevSortedReportsRef = useRef([]);

  useEffect(() => {
    const prev = prevSortedReportsRef.current;
    const changed =
      prev.length !== sortedReports.length ||
      JSON.stringify(prev) !== JSON.stringify(sortedReports);

    if (changed) {
      onSortedReportsChange?.(title, sortedReports);
      prevSortedReportsRef.current = sortedReports;
    }
  }, [sortedReports, title, onSortedReportsChange]);

  const toggleSort = (field) => {
    setSort((prev) => {
      const idx = prev.findIndex((s) => s.field === field);
      if (idx === -1) return [...prev, { field, direction: "asc" }];

      const nextDir =
        prev[idx].direction === "asc"
          ? "desc"
          : prev[idx].direction === "desc"
          ? "none"
          : "asc";

      if (nextDir === "none") return prev.filter((_, i) => i !== idx);

      const next = [...prev];
      next[idx] = { field, direction: nextDir };
      return next;
    });
  };

  const renderSortIcon = (field) => {
    const idx = sort.findIndex((s) => s.field === field);
    if (idx === -1) return <FontAwesomeIcon icon={faMinus} />;

    return (
      <FontAwesomeIcon
        icon={
          sort[idx].direction === "asc"
            ? faChevronUp
            : sort[idx].direction === "desc"
            ? faChevronDown
            : faMinus
        }
      />
    );
  };

  useEffect(() => {
    if (!tableRef.current) return;

    const timeout = setTimeout(() => {
      const rows = tableRef.current.querySelectorAll(
        `.${styles.pageRowWrapper}`
      );

      let height = 0;
      let breaks = [];

      rows.forEach((row, i) => {
        height += row.getBoundingClientRect().height;
        if (height > PAGE_HEIGHT_PX) {
          breaks.push(i);
          height = row.getBoundingClientRect().height;
        }
      });

      setPageBreakIndexes(breaks);
    }, 100);

    return () => clearTimeout(timeout);
  }, [sortedReports]);

  const updateFilter = (field, value) => {
    setSelectedFilters((prev) => {
      const options = getAllOptions(field);
      const current = prev[field] || [];

      const next =
        value === "All"
          ? current.length === options.length
            ? []
            : options
          : current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];

      return { ...prev, [field]: next };
    });
  };

  return (
    <div>
      <div className={styles.headerInfo}>
        <div className={styles.event}>{title}</div>

        <div className={styles.total}>Total Attendees: {totalAttendees}</div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.header}>
            <div className={styles.ibo} onClick={() => toggleSort("IBO")}>
              IBO {renderSortIcon("IBO")}
            </div>
            <div
              className={styles.iboALastName}
              onClick={() => toggleSort("Last Name")}
            >
              Last Name {renderSortIcon("Last Name")}
            </div>
            <div
              className={styles.iboAFirstName}
              onClick={() => toggleSort("First Name")}
            >
              First Name {renderSortIcon("First Name")}
            </div>

            <div className={styles.pin}>
              <Select
                options={["All", ...getAllOptions("ticket")]}
                onChange={(v) => updateFilter("ticket", v)}
                selectedValues={selectedFilters.ticket || []}
                placeholder="Ticket"
              />
              <div onClick={() => toggleSort("Ticket")}>
                {renderSortIcon("Ticket")}
              </div>
            </div>

            <div className={styles.diamond}>
              <Select
                options={["All", ...getAllOptions("diamond")]}
                onChange={(v) => updateFilter("diamond", v)}
                selectedValues={selectedFilters.diamond || []}
                placeholder="Diamond"
              />
              <div onClick={() => toggleSort("Diamond")}>
                {renderSortIcon("Diamond")}
              </div>
            </div>
          </div>

          {sortedReports.map((row, i) => (
            <React.Fragment key={i}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemSessionAttendance item={row} isOdd={i % 2 !== 0} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableSessionAttendance.propTypes = {
  reports: PropTypes.array,
  title: PropTypes.string,
  onSortedReportsChange: PropTypes.func,
};
